import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';

import { useFragment } from '@libs/trip-planner-interfaces';
import type {
  FragmentType,
  GraphQL,
  ItinerarySuggestionPrices,
} from '@libs/trip-planner-interfaces';
import { assertValue, exhaustiveGuard } from '@libs/utils';

import { ItinerarySuggestionPriceFragment } from '../../queries';
import { assertSmartSearchList, assertSmartSearchValue } from '../../utils';

import { toMoney } from './moneyResponseQL';

export function toItinerarySuggestionPrices(
  itinerarySuggestionPriceFragmentQL:
    | FragmentType<
        DocumentTypeDecoration<
          GraphQL.ItinerarySuggestionPriceFragment,
          unknown
        >
      >
    | null
    | undefined
): ItinerarySuggestionPrices {
  assertSmartSearchValue(itinerarySuggestionPriceFragmentQL);
  const itinerarySuggestionPricesResponse = useFragment(
    ItinerarySuggestionPriceFragment,
    itinerarySuggestionPriceFragmentQL
  );

  assertSmartSearchList(itinerarySuggestionPricesResponse.result);

  const itinerary = itinerarySuggestionPricesResponse.result.map(
    (ingredientResponseQL) => {
      switch (ingredientResponseQL.type) {
        case 'TransportIngredientResponseQL':
        case 'HotelIngredientResponseQL':
        case 'OwnStayResultQL':
        case 'OwnTransportQL': {
          assertSmartSearchValue(ingredientResponseQL.date);

          return {
            date: ingredientResponseQL.date,
          };
        }

        case 'TicketIngredientResponseQL':
          throw new Error('Standalone tickets not supported');

        case 'FerryIngredientResponseQL':
        case 'FlightIngredientResponseQL':
          throw new Error(
            'Specific transport ingredients not supported. Use Transport'
          );

        default:
          return exhaustiveGuard(
            ingredientResponseQL,
            'Smart Search ingredient response type not recognized'
          );
      }
    }
  );

  const firstIngredient = itinerary[0];
  assertValue(
    firstIngredient,
    'search-service-smart-search > itineraryPricesResponseQL.ts > firstIngredient'
  );

  const date = firstIngredient.date;

  assertSmartSearchValue(itinerarySuggestionPricesResponse.prices);

  return {
    date: new Date(date),
    totalPrice: itinerarySuggestionPricesResponse.prices.totalPrice
      ? toMoney(itinerarySuggestionPricesResponse.prices.totalPrice)
      : null,
    totalPriceWithAlternative: itinerarySuggestionPricesResponse.prices
      .totalPriceWithAlternative
      ? toMoney(
          itinerarySuggestionPricesResponse.prices.totalPriceWithAlternative
        )
      : null,
    isSuggested: itinerarySuggestionPricesResponse.isSuggested ?? false,
  };
}
